package com.tovtalk.ui.platform

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onPlaced
import androidx.compose.ui.layout.positionInRoot
import com.tovtalk.data.cache.MediaCacheManager
import com.tovtalk.data.cache.S3CacheKeyResolver
import com.tovtalk.utils.viewportHeightPx
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event

private const val REEL_CONTAINER_ID = "tovtalk-reel-container"
// Must match TovTalkSpacing.bottomNavHeight / DesignTokens.Spacing.bottomNavHeight (64)
private const val BOTTOM_NAV_PX = 64

/** Escape user-controlled text before interpolating into innerHTML (prevents XSS in the feed). */
private fun escapeHtml(s: String): String = s
    .replace("&", "&amp;")
    .replace("<", "&lt;")
    .replace(">", "&gt;")
    .replace("\"", "&quot;")
    .replace("'", "&#39;")

/**
 * Mutable holder for the current reel callbacks. DOM click handlers read the latest callbacks from
 * here, so we can wire handlers once (structure effect) without re-keying on lambda identity.
 */
private class ReelCallbacks {
    var onLikeClick: () -> Unit = {}
    var onCommentClick: () -> Unit = {}
    var onShareClick: () -> Unit = {}
    var onUserClick: () -> Unit = {}
    var onFollowClick: () -> Unit = {}
    var onTap: () -> Unit = {}
    var onDoubleTap: () -> Unit = {}
    var onVideoEnded: () -> Unit = {}
}

/** Last placement state so we can re-apply reel position on visualViewport resize/scroll (mobile address bar, keyboard). */
private data class ReelPlacementState(
    val composePosX: Float,
    val composePosY: Float,
    val composeSizeW: Int,
    val composeSizeH: Int,
    val applyBottomNavSafeArea: Boolean,
)
private var lastReelPlacement: ReelPlacementState? = null
private var lastReelContainer: org.w3c.dom.HTMLElement? = null

/** Guard: register the reel viewport listeners exactly once (the container is a page-lifetime singleton). */
private var reelViewportListenersRegistered = false
/** rAF id to coalesce reposition work to one per frame. */
private var reelRepositionRafId: Int = 0

private fun scheduleReelReposition() {
    if (reelRepositionRafId != 0) return
    reelRepositionRafId = window.requestAnimationFrame { _ ->
        reelRepositionRafId = 0
        reapplyReelPositionFromStoredState()
    }
}

private fun registerReelViewportListenersOnce() {
    if (reelViewportListenersRegistered) return
    reelViewportListenersRegistered = true
    // Re-apply reel position on viewport resize/scroll (mobile address bar, keyboard), throttled via rAF.
    val onResize: (Event) -> Unit = { scheduleReelReposition() }
    window.addEventListener("resize", onResize)
    val vv = window.asDynamic().visualViewport
    if (vv != null) {
        vv.addEventListener("resize", onResize)
        vv.addEventListener("scroll", onResize)
    }
}

internal fun canvasViewportOffset(): Pair<Double, Double> {
    val canvasEl = document.querySelector("canvas") ?: document.getElementById("root")?.firstElementChild
    return if (canvasEl != null && canvasEl is org.w3c.dom.HTMLElement) {
        val rect = canvasEl.getBoundingClientRect()
        rect.left to rect.top
    } else {
        0.0 to 0.0
    }
}

private fun computeReelGeometry(
    composePosX: Float,
    composePosY: Float,
    composeSizeW: Int,
    composeSizeH: Int,
    applyBottomNavSafeArea: Boolean,
): ReelGeometry {
    val (canvasLeft, canvasTop) = canvasViewportOffset()
    val vh = viewportHeightPx()
    val maxH = if (applyBottomNavSafeArea) (vh - BOTTOM_NAV_PX).coerceAtLeast(1) else vh
    val reelWidth = composeSizeW.coerceAtLeast(1)
    val height9x16 = (reelWidth * 16 / 9).toInt()
    val absTop = canvasTop + composePosY
    val reelHeight = minOf(
        composeSizeH,
        height9x16,
        maxH,
        (maxH - absTop).toInt().coerceAtLeast(1)
    )
    val viewportLeft = canvasLeft + composePosX
    val viewportTop = absTop + (composeSizeH - reelHeight) / 2.0
    return ReelGeometry(viewportLeft, viewportTop, reelWidth, reelHeight)
}

private data class ReelGeometry(
    val viewportLeft: Double,
    val viewportTop: Double,
    val width: Int,
    val height: Int,
)

private fun reapplyReelPositionFromStoredState() {
    val container = lastReelContainer ?: return
    if (container.asDynamic().style.display == "none") return
    val state = lastReelPlacement ?: return
    val g = computeReelGeometry(
        state.composePosX, state.composePosY,
        state.composeSizeW, state.composeSizeH,
        state.applyBottomNavSafeArea,
    )
    applyReelPosition(container, g.viewportLeft, g.viewportTop, g.width, g.height)
}

@Composable
actual fun WebReelCell(
    modifier: Modifier,
    videoUrl: String?,
    isVisible: Boolean,
    isPlaying: Boolean,
    videoMuted: Boolean,
    isLiked: Boolean,
    likeCount: String,
    commentCount: String,
    shareCount: String,
    username: String,
    avatarUrl: String?,
    description: String?,
    musicTitle: String?,
    viewCountText: String?,
    showFollowButton: Boolean,
    followButtonText: String,
    showDoubleTapHeart: Boolean,
    onLikeClick: () -> Unit,
    onCommentClick: () -> Unit,
    onShareClick: () -> Unit,
    onUserClick: () -> Unit,
    onFollowClick: () -> Unit,
    onTap: () -> Unit,
    onDoubleTap: () -> Unit,
    onVideoEnded: () -> Unit,
) {
    val dom = remember { createReelDom() }
    val callbacks = remember { ReelCallbacks() }
    // Mobile: full-screen reel above bottom nav; wide-screen: Compose wraps us in a 9:16 box and
    // renders FeedSideActions separately, so we skip bottom-nav clamp, hide the DOM action column,
    // and disable pointer events on the container so Compose (pager swipe, tap-to-play) handles input.
    val isMobileLayout = com.tovtalk.ui.adaptive.isMobile()
    val hideActions = !isMobileLayout
    val allowPointerEvents = isMobileLayout

    // Route avatar through the shared media cache so it doesn't refetch on S3 signature rotation (A6).
    var resolvedAvatarUrl by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(avatarUrl) {
        resolvedAvatarUrl = avatarUrl?.takeIf { it.isNotBlank() }?.let {
            try { MediaCacheManager.getImageUrl(it) } catch (_: Throwable) { it }
        }
    }

    // Keep the callback holder pointing at the latest lambdas every recomposition (cheap, no DOM work).
    callbacks.onLikeClick = onLikeClick
    callbacks.onCommentClick = onCommentClick
    callbacks.onShareClick = onShareClick
    callbacks.onUserClick = onUserClick
    callbacks.onFollowClick = onFollowClick
    callbacks.onTap = onTap
    callbacks.onDoubleTap = onDoubleTap
    callbacks.onVideoEnded = onVideoEnded

    // (a) Structure + handler effect — NOT keyed on lambda identity. Rebuilds the overlay markup and
    // (re)binds click handlers when the structural shape or displayed text changes. Handlers read live
    // callbacks from the holder, so changing a lambda no longer rebuilds innerHTML.
    DisposableEffect(
        isVisible, hideActions, showFollowButton, resolvedAvatarUrl,
        username, description, musicTitle
    ) {
        applyReelStructure(
            dom = dom,
            isVisible = isVisible,
            avatarUrl = resolvedAvatarUrl,
            username = username,
            description = description,
            musicTitle = musicTitle,
            showFollowButton = showFollowButton,
            hideActions = hideActions,
            callbacks = callbacks,
        )
        onDispose {
            // Do NOT hide/clear the shared container here. The container is shared; only the visible
            // cell should control it. Leaving it visible is safe; the re-run of the effect refreshes it.
        }
    }

    // (b) Cheap text/media update effect — keyed only on display strings + media state.
    DisposableEffect(
        videoUrl, isVisible, isPlaying, videoMuted, isLiked, likeCount, commentCount, shareCount,
        showDoubleTapHeart, hideActions
    ) {
        applyReelTextAndMedia(
            dom = dom,
            videoUrl = videoUrl,
            isVisible = isVisible,
            isPlaying = isPlaying,
            videoMuted = videoMuted,
            isLiked = isLiked,
            likeCount = likeCount,
            commentCount = commentCount,
            shareCount = shareCount,
            showDoubleTapHeart = showDoubleTapHeart,
            hideActions = hideActions,
        )
        onDispose { }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .onPlaced { coordinates ->
                if (!isVisible) return@onPlaced
                val size = coordinates.size
                val pos = coordinates.positionInRoot()
                val g = computeReelGeometry(
                    pos.x, pos.y, size.width, size.height,
                    applyBottomNavSafeArea = isMobileLayout,
                )
                lastReelPlacement = ReelPlacementState(pos.x, pos.y, size.width, size.height, isMobileLayout)
                lastReelContainer = dom.container
                applyReelPosition(dom.container, g.viewportLeft, g.viewportTop, g.width, g.height)
                dom.container.asDynamic().style.pointerEvents = if (allowPointerEvents) "auto" else "none"
                if (!videoUrl.isNullOrBlank()) {
                    dom.container.asDynamic().style.display = "block"
                }
            }
    )
}

// --- DOM structure (one container: video + overlay) ---

private data class ReelDom(
    val container: org.w3c.dom.HTMLElement,
    val video: HTMLVideoElement,
    val overlay: org.w3c.dom.HTMLElement,
    val playBtn: org.w3c.dom.HTMLElement,
    val heartEl: org.w3c.dom.HTMLElement,
    val leftCol: org.w3c.dom.HTMLElement,
    val likeBtn: org.w3c.dom.HTMLElement,
    val commentBtn: org.w3c.dom.HTMLElement,
    val shareBtn: org.w3c.dom.HTMLElement,
)

private fun createReelDom(): ReelDom {
    var container = document.getElementById(REEL_CONTAINER_ID) as? org.w3c.dom.HTMLElement
    if (container == null) {
        container = document.createElement("div") as org.w3c.dom.HTMLElement
        container.id = REEL_CONTAINER_ID
        container.className = "reel-container"
        val s = container.asDynamic().style
        s.position = "fixed"
        s.left = "0"
        s.top = "0"
        s.width = "0"
        s.height = "0"
        s.overflow = "hidden"
        s.display = "none"
        document.body!!.appendChild(container)
        registerReelViewportListenersOnce()
    }

    var video = container.querySelector("video") as? HTMLVideoElement
    if (video == null) {
        video = document.createElement("video") as HTMLVideoElement
        video.setAttribute("playsinline", "true")
        video.setAttribute("webkit-playsinline", "true")
        video.muted = true
        // Explicit sizing so the <video> fills the reel container (otherwise browser uses intrinsic
        // video dimensions and on desktop the video renders tiny in a corner of the huge container).
        video.style.setProperty("position", "absolute")
        video.style.setProperty("left", "0")
        video.style.setProperty("top", "0")
        video.style.setProperty("width", "100%")
        video.style.setProperty("height", "100%")
        video.style.setProperty("object-fit", "contain")
        video.style.setProperty("background", "#000")
        container.appendChild(video)
    }

    var overlay = container.querySelector(".reel-overlay") as? org.w3c.dom.HTMLElement
    if (overlay == null) {
        overlay = document.createElement("div") as org.w3c.dom.HTMLElement
        overlay.className = "reel-overlay"
        val os = overlay.asDynamic().style
        os.position = "absolute"
        os.left = "0"
        os.top = "0"
        os.width = "100%"
        os.height = "100%"
        os.pointerEvents = "none"
        container.appendChild(overlay)
    }

    var playBtn = overlay.querySelector(".reel-play") as? org.w3c.dom.HTMLElement
    if (playBtn == null) {
        playBtn = document.createElement("div") as org.w3c.dom.HTMLElement
        playBtn.className = "reel-play"
        playBtn.textContent = "▶"
        overlay.appendChild(playBtn)
    }

    var heartEl = overlay.querySelector(".reel-heart") as? org.w3c.dom.HTMLElement
    if (heartEl == null) {
        heartEl = document.createElement("div") as org.w3c.dom.HTMLElement
        heartEl.className = "reel-heart"
        heartEl.textContent = "♥"
        overlay.appendChild(heartEl)
    }

    var bottomRow = overlay.querySelector(".reel-bottom") as? org.w3c.dom.HTMLElement
    if (bottomRow == null) {
        bottomRow = document.createElement("div") as org.w3c.dom.HTMLElement
        bottomRow.className = "reel-bottom"
        val bs = bottomRow.asDynamic().style
        bs.position = "absolute"
        bs.left = "12px"
        bs.right = "12px"
        bs.bottom = "12px"
        bs.display = "flex"
        bs.alignItems = "flex-end"
        bs.justifyContent = "space-between"
        bs.color = "#fff"
        bs.pointerEvents = "none"
        overlay.appendChild(bottomRow)
    }

    var leftCol = bottomRow.querySelector(".reel-left") as? org.w3c.dom.HTMLElement
    if (leftCol == null) {
        leftCol = document.createElement("div") as org.w3c.dom.HTMLElement
        leftCol.className = "reel-left"
        val ls = leftCol.asDynamic().style
        ls.flex = "1 1 auto"
        ls.minWidth = "0"
        ls.maxWidth = "100%"
        ls.pointerEvents = "auto"
        ls.textShadow = "0 1px 2px rgba(0,0,0,0.8)"
        bottomRow.appendChild(leftCol)
    }

    var actionsCol = bottomRow.querySelector(".reel-actions") as? org.w3c.dom.HTMLElement
    if (actionsCol == null) {
        actionsCol = document.createElement("div") as org.w3c.dom.HTMLElement
        actionsCol.className = "reel-actions"
        bottomRow.appendChild(actionsCol)
    }

    var likeBtn = actionsCol.querySelector(".reel-action-btn.reel-like") as? org.w3c.dom.HTMLElement
    if (likeBtn == null) {
        likeBtn = document.createElement("div") as org.w3c.dom.HTMLElement
        likeBtn.className = "reel-action-btn reel-like"
        actionsCol.appendChild(likeBtn)
    }
    var commentBtn = actionsCol.querySelector(".reel-action-btn.reel-comment") as? org.w3c.dom.HTMLElement
    if (commentBtn == null) {
        commentBtn = document.createElement("div") as org.w3c.dom.HTMLElement
        commentBtn.className = "reel-action-btn reel-comment"
        actionsCol.appendChild(commentBtn)
    }
    var shareBtn = actionsCol.querySelector(".reel-action-btn.reel-share") as? org.w3c.dom.HTMLElement
    if (shareBtn == null) {
        shareBtn = document.createElement("div") as org.w3c.dom.HTMLElement
        shareBtn.className = "reel-action-btn reel-share"
        actionsCol.appendChild(shareBtn)
    }

    return ReelDom(container, video, overlay, playBtn, heartEl, leftCol, likeBtn, commentBtn, shareBtn)
}

private fun applyReelPosition(
    container: org.w3c.dom.HTMLElement,
    viewportLeft: Double,
    viewportTop: Double,
    width: Int,
    height: Int
) {
    val s = container.asDynamic().style
    s.left = "${viewportLeft}px"
    s.top = "${viewportTop}px"
    s.width = "${width}px"
    s.height = "${height}px"
}

/**
 * Structure + handler binding. Rebuilds the leftCol markup and (re)binds click handlers. Handlers read
 * live callbacks from [callbacks] so this does NOT need to re-run when only a lambda identity changes.
 */
private fun applyReelStructure(
    dom: ReelDom,
    isVisible: Boolean,
    avatarUrl: String?,
    username: String,
    description: String?,
    musicTitle: String?,
    showFollowButton: Boolean,
    hideActions: Boolean,
    callbacks: ReelCallbacks,
) {
    // When not visible, do NOT touch the shared container: other pages share the same DOM.
    if (!isVisible) return

    dom.leftCol.innerHTML = buildString {
        append("<div class=\"reel-user-row\" style='display:flex;align-items:center;gap:var(--space-xs,8px);margin-bottom:var(--space-xs,8px);cursor:pointer'>")
        if (!avatarUrl.isNullOrBlank() && !hideActions) {
            append("<img class=\"reel-avatar\" src=\"${avatarUrl.replace("\"", "&quot;")}\" alt=\"\" />")
        }
        if (showFollowButton && !hideActions) {
            append("<button type='button' class=\"reel-follow-plus\" aria-label=\"Follow\">+</button>")
        }
        append("<span style='font-weight:600'>@${escapeHtml(username)}</span>")
        append("</div>")
        musicTitle?.let { append("<div style='margin-bottom:4px;opacity:0.7;font-size:12px'>♪ ${escapeHtml(it)}</div>") }
        description?.takeIf { it.isNotBlank() }?.let { desc ->
            val escaped = escapeHtml(desc)
            append("<div class=\"reel-desc-wrap\" style='background:rgba(0,0,0,0.75);border-radius:8px;padding:6px 8px;margin-top:6px;cursor:pointer'><span class=\"reel-desc-text\" style='display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;opacity:0.95'>$escaped</span></div>")
        }
    }

    dom.video.onended = { _ -> callbacks.onVideoEnded() }

    dom.likeBtn.onclick = { ev: Event ->
        ev.stopPropagation()
        ev.preventDefault()
        callbacks.onLikeClick()
    }
    dom.commentBtn.onclick = { ev: Event ->
        ev.stopPropagation()
        ev.preventDefault()
        callbacks.onCommentClick()
    }
    dom.shareBtn.onclick = { ev: Event ->
        ev.stopPropagation()
        ev.preventDefault()
        callbacks.onShareClick()
    }
    dom.playBtn.onclick = { ev: Event ->
        ev.stopPropagation()
        ev.preventDefault()
        callbacks.onTap()
    }

    var lastTap = 0.0
    dom.overlay.onclick = { ev: Event ->
        ev.preventDefault()
        val now = window.performance.now()
        if (now - lastTap < 350) {
            callbacks.onDoubleTap()
            lastTap = 0.0
        } else {
            callbacks.onTap()
            lastTap = now
        }
    }

    dom.leftCol.querySelector(".reel-user-row")?.let { row ->
        (row as org.w3c.dom.HTMLElement).onclick = { ev: Event ->
            ev.stopPropagation()
            ev.preventDefault()
            callbacks.onUserClick()
        }
    }
    dom.leftCol.querySelector(".reel-follow-plus")?.let { btn ->
        (btn as org.w3c.dom.HTMLElement).onclick = { ev: Event ->
            ev.stopPropagation()
            ev.preventDefault()
            callbacks.onFollowClick()
        }
    }
    dom.leftCol.querySelector(".reel-desc-wrap")?.let { wrap ->
        (wrap as org.w3c.dom.HTMLElement).onclick = { ev: Event ->
            ev.stopPropagation()
            ev.preventDefault()
            wrap.classList.toggle("reel-desc-expanded")
            val textEl = wrap.querySelector(".reel-desc-text") as? org.w3c.dom.HTMLElement
            textEl?.style?.setProperty("white-space", if (wrap.classList.contains("reel-desc-expanded")) "pre-wrap" else "nowrap")
        }
    }
}

/** Cheap per-state update: video source/playback + counts. No innerHTML rebuild of the leftCol. */
private fun applyReelTextAndMedia(
    dom: ReelDom,
    videoUrl: String?,
    isVisible: Boolean,
    isPlaying: Boolean,
    videoMuted: Boolean,
    isLiked: Boolean,
    likeCount: String,
    commentCount: String,
    shareCount: String,
    showDoubleTapHeart: Boolean,
    hideActions: Boolean,
) {
    // When not visible, do NOT touch the shared container: other pages share the same DOM.
    if (!isVisible) return

    dom.container.asDynamic().style.display = "block"

    val v = dom.video
    v.muted = videoMuted
    if (!videoUrl.isNullOrBlank()) {
        // Compare STABLE cache keys (scheme+host+path, signature params dropped) instead of the raw
        // URLs. S3 re-signs URLs frequently; without this, every signature rotation produced a new
        // src string and forced the <video> to reload from scratch (stutter / black flash) even
        // though it points at the exact same object. Only reassign when the underlying object changes.
        val currentSrc = v.src
        val sameObject = currentSrc.isNotBlank() &&
            S3CacheKeyResolver.resolve(currentSrc) == S3CacheKeyResolver.resolve(videoUrl)
        if (!sameObject) {
            v.src = videoUrl
            v.load()
        }
        if (isPlaying) v.play()?.catch { } else v.pause()
    } else {
        v.pause()
        v.removeAttribute("src")
    }

    dom.playBtn.asDynamic().style.display = if (!isPlaying) "flex" else "none"
    dom.heartEl.asDynamic().style.display = if (showDoubleTapHeart) "block" else "none"

    // Hide the DOM action column (like/comment/share) and follow-plus on wide-screen — Compose renders
    // FeedSideActions next to the 9:16 video instead. Caller passes hideActions=true in that case.
    dom.likeBtn.parentElement?.asDynamic()?.style?.display = if (hideActions) "none" else "flex"

    dom.likeBtn.className = "reel-action-btn reel-like" + if (isLiked) " reel-action-liked" else ""
    dom.likeBtn.innerHTML = "<span>${if (isLiked) "♥" else "♡"}</span><span>$likeCount</span>"
    dom.commentBtn.innerHTML = "<span>💬</span><span>$commentCount</span>"
    dom.shareBtn.innerHTML = "<span>↗</span><span>$shareCount</span>"
}
